import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Divider,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from '@mui/material';
import RiskEvidencePlayer from '../events/RiskEvidencePlayer';
import EventSnapshotThumb from '../events/EventSnapshotThumb';
import EventSnapshotBboxEditPanel from '../events/EventSnapshotBboxEditPanel';
import { fetchEventRiskClip, isDrowningEventType, resolveEvidenceEventType } from '../../services/riskClipService';
import {
  Notifications as NotificationsIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Sms as SmsIcon,
  DeleteSweep as DeleteSweepIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { useEventContext, Event } from '../../contexts/EventContext';
import api from '../../services/api';
import { eventService, formatEvidenceBytes } from '../../services/eventService';
import type { OrphanEvidencePurgeResult } from '../../services/eventService';
import { monitoringWebSocket } from '../../services/websocketService';
import {
  resolveEventDbId,
  safetyAlertDispatchService,
} from '../../services/safetyAlertDispatchService';
import { SafetyAlertByEventDialog } from '../safety/SafetyAlertDispatchHistoryCard';
import { EVENT_LOG_LIST_MAX_HEIGHT } from '../../constants/scrollPanel';
import WidgetListPagination from '../common/WidgetListPagination';
import FolderDeleteOutlinedIcon from '@mui/icons-material/FolderDeleteOutlined';
import DialogContentText from '@mui/material/DialogContentText';

function mapEvidenceManifestStatus(
  raw: string
): 'pending' | 'archiving' | 'ready' | 'empty' {
  const s = String(raw || '').toLowerCase();
  if (s === 'ready') return 'ready';
  if (s === 'empty') return 'empty';
  if (s === 'processing' || s === 'archiving') return 'archiving';
  return 'pending';
}

type RiskEvidenceWsPayload = {
  event_id?: number | string;
  pool_id?: number | string;
  status?: string;
};

interface EventLogWidgetProps {
  maxItems?: number;
  title?: string;
  subtitle?: string;
  poolId?: string;
  showRefresh?: boolean;
  showFilter?: boolean;
  showViewAll?: boolean;
  listMaxHeight?: number | string;
  /** true면 그리드 열 높이에 맞춤(스크롤 영역이 남는 공간을 채움). 기본은 내용만큼만 높이 증가 */
  fillHeight?: boolean;
  /** 선택 삭제·전체 삭제 버튼 및 체크박스 */
  showBulkDelete?: boolean;
  /**
   * 이벤트 리스트에 없는 gateway_evidence(고아 스테이징·삭제된 이벤트 폴더) 일괄 정리.
   * 기본: showBulkDelete 와 동일
   */
  showOrphanEvidencePurge?: boolean;
  /** 설정 시 카드에 페이지당 건수만 표시하고 이전/다음·전체보기(팝업) 제공 */
  pageSize?: number;
  fullViewTitle?: string;
}

const EventLogWidget: React.FC<EventLogWidgetProps> = ({
  maxItems = 5,
  title = '이벤트 로그',
  subtitle = '최근 발생한 이벤트',
  poolId,
  showRefresh = true,
  showFilter = true,
  showViewAll = true,
  listMaxHeight = EVENT_LOG_LIST_MAX_HEIGHT,
  fillHeight = false,
  showBulkDelete = false,
  showOrphanEvidencePurge,
  pageSize,
  fullViewTitle = '전체 이벤트',
}) => {
  const allowOrphanPurge = showOrphanEvidencePurge ?? showBulkDelete;
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { 
    events,
    loading, 
    unreadCount, 
    markAsRead,
    updateEventStatus,
    acknowledgeEvent,
    deleteEvent,
    bulkDeleteEvents,
    deleteAllEvents,
    fetchEvents,
    patchEventImageUrl,
    patchEventDisplay,
    setFilter,
    clearFilter
  } = useEventContext();
  const [localLoading, setLocalLoading] = useState(false);
  const [detailEvent, setDetailEvent] = useState<Event | null>(null);
  const [evidenceStatusMap, setEvidenceStatusMap] = useState<Record<string, string>>({});
  const evidenceStatusMapRef = useRef<Record<string, string>>({});
  evidenceStatusMapRef.current = evidenceStatusMap;
  const [allFilteredEvents, setAllFilteredEvents] = useState<Event[]>([]);
  const [listPage, setListPage] = useState(0);
  const [fullListOpen, setFullListOpen] = useState(false);
  const [cctvLocationMap, setCctvLocationMap] = useState<Record<string, string>>({});
  const [safetyAlertCounts, setSafetyAlertCounts] = useState<Record<number, number>>({});
  const [safetyAlertDialog, setSafetyAlertDialog] = useState<{
    eventId: number;
    title: string;
  } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [orphanPreview, setOrphanPreview] = useState<OrphanEvidencePurgeResult | null>(null);
  const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
  const [orphanBusy, setOrphanBusy] = useState(false);
  /** 캡처 bbox 저장 후 목록 썸네일 강제 갱신 */
  const [snapshotThumbBust, setSnapshotThumbBust] = useState<Record<string, number>>({});
  const [activeFilters, setActiveFilters] = useState<{
    poolId?: string;
    onlyUnread: boolean;
    type?: string[];
  }>({
    poolId: poolId,
    onlyUnread: false,
    type: undefined
  });

  // 상세 다이얼로그가 열린 동안 목록에 캡처 URL이 새로 생기면 반영 (편집 cache-bust URL은 유지)
  useEffect(() => {
    if (!detailEvent) return;
    const latest = events.find((e) => e.id === detailEvent.id);
    if (!latest?.imageUrl) return;
    const strip = (u: string) => u.split('?')[0];
    const latestBase = strip(latest.imageUrl);
    const detailBase = strip(detailEvent.imageUrl || '');
    if (latestBase !== detailBase) {
      setDetailEvent((prev) => (prev ? { ...prev, imageUrl: latest.imageUrl } : prev));
      return;
    }
    // 같은 경로인데 목록 쪽이 더 최신 bust면 반영
    if (latest.imageUrl.includes('?t=') && latest.imageUrl !== detailEvent.imageUrl) {
      setDetailEvent((prev) => (prev ? { ...prev, imageUrl: latest.imageUrl } : prev));
    }
  }, [events, detailEvent]);

  // 이벤트를 필터링하여 표시할 이벤트 목록 설정
  useEffect(() => {
    // 필터 조건에 따라 이벤트 필터링
    let filtered = [...events];
    
    // 특정 시설 필터 (문자열/숫자 혼용 대비)
    if (activeFilters.poolId != null && String(activeFilters.poolId).trim() !== '') {
      const want = String(activeFilters.poolId);
      filtered = filtered.filter((event) => String(event.poolId) === want);
    }
    
    // 읽지 않은 이벤트만 필터
    if (activeFilters.onlyUnread) {
      filtered = filtered.filter(event => !event.isRead);
    }
    
    // 이벤트 타입 필터
    if (activeFilters.type && activeFilters.type.length > 0) {
      filtered = filtered.filter(event => activeFilters.type?.includes(event.type));
    }
    
    // 최신 이벤트 순서로 정렬
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    setAllFilteredEvents(filtered.slice(0, maxItems));
  }, [events, activeFilters, maxItems, poolId]);

  useEffect(() => {
    setListPage(0);
  }, [allFilteredEvents.length, activeFilters.poolId, activeFilters.onlyUnread, pageSize]);

  const paginated = pageSize != null && pageSize > 0;
  const pageCount = paginated
    ? Math.max(1, Math.ceil(allFilteredEvents.length / pageSize))
    : 1;
  const safePage = paginated ? Math.min(listPage, pageCount - 1) : 0;
  const visibleEvents = paginated
    ? allFilteredEvents.slice(safePage * pageSize, safePage * pageSize + pageSize)
    : allFilteredEvents;

  // 마운트·poolId 변경 시: 공유 EventContext 시설 필터를 위젯 범위에 맞춤
  // (다른 화면에서 건 pool 필터가 대시보드에 남아 타 시설 이벤트가 빠지던 문제 방지)
  useEffect(() => {
    if (poolId != null && String(poolId).trim() !== '') {
      const pid = String(poolId);
      setActiveFilters((prev) => ({ ...prev, poolId: pid }));
      setFilter({ poolId: pid });
    } else {
      setActiveFilters((prev) => {
        const next = { ...prev };
        delete next.poolId;
        return next;
      });
      setFilter({ poolId: undefined });
    }
    void handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pool 범위 변경 시에만
  }, [poolId]);

  useEffect(() => {
    let cancelled = false;
    const loadCctvLocations = async () => {
      try {
        const rows = await api.get<any[]>('/api/cctvs/');
        if (cancelled) return;
        const map: Record<string, string> = {};
        const payload = rows as
          | any[]
          | { data?: any[]; items?: any[]; cctvs?: any[]; pools?: any[]; success?: boolean };
        const list = Array.isArray(payload)
          ? payload
          : payload?.data ?? payload?.items ?? payload?.cctvs ?? [];
        for (const row of list) {
          const id = row?.id;
          const loc = String(row?.location || '').trim();
          const code = String(row?.code || '').trim();
          if (id != null && loc) map[String(id)] = loc;
          const poolId =
            row?.pool_id != null ? String(row.pool_id) : '';
          if (code && loc && poolId) {
            map[`pool:${poolId}:code:${code.toUpperCase()}`] = loc;
          }
        }
        setCctvLocationMap(map);
      } catch {
        setCctvLocationMap({});
      }
    };
    loadCctvLocations();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ids = allFilteredEvents
      .map((e) => resolveEventDbId(e))
      .filter((id): id is number => id != null);
    if (ids.length === 0) {
      setSafetyAlertCounts({});
      return;
    }
    let cancelled = false;
    const poolNum =
      poolId != null && poolId !== '' ? Number(poolId) : undefined;
    void safetyAlertDispatchService
      .eventSummary(ids, Number.isFinite(poolNum) ? poolNum : undefined)
      .then((counts) => {
        if (!cancelled) setSafetyAlertCounts(counts);
      })
      .catch(() => {
        if (!cancelled) setSafetyAlertCounts({});
      });
    return () => {
      cancelled = true;
    };
  }, [allFilteredEvents, poolId]);

  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(visibleEvents.map((e) => e.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      return next;
    });
  }, [visibleEvents]);

  /** overflow:auto 영역이 실제로 스크롤되지 않을 때(예: 10건이 maxHeight에 딱 맞을 때) 휠이 페이지로 전달되지 않는 문제 보정 */
  const handleListWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const dy = e.deltaY;
    const hasInnerScroll = scrollHeight > clientHeight + 1;

    if (!hasInnerScroll) {
      let forwarded = false;
      let node: HTMLElement | null = el.parentElement;
      while (node) {
        const style = window.getComputedStyle(node);
        const canScrollY =
          (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
          node.scrollHeight > node.clientHeight + 1;
        if (canScrollY) {
          node.scrollTop += dy;
          e.preventDefault();
          forwarded = true;
          break;
        }
        node = node.parentElement;
      }
      if (!forwarded) {
        window.scrollBy(0, dy);
        e.preventDefault();
      }
      return;
    }

    if (dy < 0 && scrollTop <= 0) return;
    if (dy > 0 && scrollTop + clientHeight >= scrollHeight - 1) return;
    e.stopPropagation();
  };

  const allSelected =
    visibleEvents.length > 0 && selected.size === visibleEvents.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleEvents.map((e) => e.id)));
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`선택한 ${selected.size}건의 이벤트 삭제 확인`)) return;
    setBulkBusy(true);
    try {
      const n = await bulkDeleteEvents(Array.from(selected), poolId);
      enqueueSnackbar(`${n}건 삭제함`, { variant: 'success' });
      setSelected(new Set());
      if (detailEvent && selected.has(detailEvent.id)) setDetailEvent(null);
    } catch {
      enqueueSnackbar('선택 삭제에 실패', { variant: 'error' });
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDeleteAllEvents = async () => {
    const scopeLabel = poolId ? '이 시설의 ' : '';
    if (!window.confirm(`${scopeLabel}저장된 이벤트 전체 삭제 확인. 되돌리기 불가`)) return;
    setBulkBusy(true);
    try {
      const n = await deleteAllEvents(poolId);
      enqueueSnackbar(`${n}건 삭제함`, { variant: 'success' });
      setSelected(new Set());
      setDetailEvent(null);
      await fetchEvents();
    } catch {
      enqueueSnackbar('전체 삭제에 실패', { variant: 'error' });
    } finally {
      setBulkBusy(false);
    }
  };

  const handleOpenOrphanEvidencePurge = async () => {
    setOrphanBusy(true);
    try {
      const preview = await eventService.previewOrphanEvidence();
      setOrphanPreview(preview);
      setOrphanDialogOpen(true);
      if ((preview.orphan_dirs ?? 0) === 0) {
        enqueueSnackbar('삭제할 미연결 증거 없음', { variant: 'info' });
      }
    } catch {
      enqueueSnackbar('미연결 증거 조회에 실패', { variant: 'error' });
    } finally {
      setOrphanBusy(false);
    }
  };

  const handleConfirmOrphanEvidencePurge = async () => {
    setOrphanBusy(true);
    try {
      const result = await eventService.purgeOrphanEvidence();
      const dirs = result.deleted_dirs ?? 0;
      const bytes = formatEvidenceBytes(result.deleted_bytes ?? 0);
      enqueueSnackbar(`미연결 증거 ${dirs}개 폴더(${bytes})를 삭제함`, {
        variant: 'success',
      });
      setOrphanDialogOpen(false);
      setOrphanPreview(null);
    } catch {
      enqueueSnackbar('미연결 증거 삭제에 실패', { variant: 'error' });
    } finally {
      setOrphanBusy(false);
    }
  };

  // 이벤트 새로고침
  const handleRefresh = async () => {
    setLocalLoading(true);
    await fetchEvents();
    setLocalLoading(false);
  };

  // 필터 토글
  const toggleUnreadFilter = () => {
    const newState = !activeFilters.onlyUnread;
    setActiveFilters(prev => ({ ...prev, onlyUnread: newState }));
    setFilter({ isRead: newState ? false : undefined });
  };

  // 이벤트 클릭 처리 — 열람 시 '신규' 해제 (읽음 + 확인/처리완료)
  const handleEventClick = (event: Event) => {
    setDetailEvent(event);
    if (!event.isRead) {
      markAsRead(event.id);
    }
    if (event.status === 'new') {
      // 즉시 UI에서 신규 제거 (ACK 전에도)
      updateEventStatus(event.id, 'processing');
      void acknowledgeEvent(event.id, false, '리스트에서 확인').catch(() => {
        // ACK 실패 시에도 로컬은 처리중 유지
      });
    }
  };

  const eventStatusChipLabel = (event: Event): string => {
    if (event.status === 'resolved') return '해결됨';
    if (event.status === 'processing' || event.isRead) return '확인';
    return '신규';
  };

  const handleDeleteEvent = async (event: Event) => {
    const ok = window.confirm('이 이벤트를 삭제 확인');
    if (!ok) return;
    try {
      await deleteEvent(event.id);
      setDetailEvent(null);
    } catch {
      // EventContext에서 오류 상태를 관리함
    }
  };

  const formatRelativeTime = (timestamp: Date) => {
    const now = new Date();
    const eventDate = new Date(timestamp);
    const diffMs = now.getTime() - eventDate.getTime();
    const diffMins = Math.round(diffMs / 60000);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffMins < 24 * 60) return `${Math.floor(diffMins / 60)}시간 전`;
    if (diffMins < 24 * 60 * 7) return `${Math.floor(diffMins / (24 * 60))}일 전`;
    return '';
  };

  const formatAbsoluteTime = (timestamp: Date) => {
    const d = new Date(timestamp);
    if (!Number.isFinite(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const formatEventTimeLabel = (timestamp: Date) => {
    // 리스트는 절대시각만 (상대시간은 툴팁)
    return formatAbsoluteTime(timestamp);
  };

  const setEvidenceStatus = (eventId: string, status: string) => {
    setEvidenceStatusMap((prev) => (prev[eventId] === status ? prev : { ...prev, [eventId]: status }));
  };

  const getEvidenceEventId = (event: Event): string | number => {
    return (
      (event.metadata?.event_db_id as number | string | undefined) ??
      event.ack_id ??
      event.id
    );
  };

  useEffect(() => {
    let cancelled = false;
    const loadEvidenceStatuses = async () => {
      const drowningEvents = allFilteredEvents
        .filter((event) =>
          isDrowningEventType(resolveEvidenceEventType(event))
        )
        .slice(0, 30);

      if (drowningEvents.length === 0) return;

      const results = await Promise.all(
        drowningEvents.map(async (event) => {
          const prev = evidenceStatusMapRef.current[event.id];
          if (prev === 'ready' || prev === 'empty') {
            return [event.id, prev] as const;
          }
          try {
            const manifest = await fetchEventRiskClip(getEvidenceEventId(event));
            const status = mapEvidenceManifestStatus(String(manifest?.status || ''));
            return [event.id, status] as const;
          } catch {
            return [event.id, 'pending'] as const;
          }
        })
      );

      if (cancelled) return;
      setEvidenceStatusMap((prev) => {
        const next = { ...prev };
        for (const [eventId, status] of results) {
          next[eventId] = status;
        }
        return next;
      });
    };

    loadEvidenceStatuses();
    const hasDrowning = allFilteredEvents.some((event) =>
      isDrowningEventType(resolveEvidenceEventType(event))
    );
    let timer: ReturnType<typeof setInterval> | undefined;
    if (hasDrowning) {
      timer = setInterval(() => {
        void loadEvidenceStatuses();
      }, 8000);
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 목록 변경 시에만 재구독; 상태는 ref로 스킵
  }, [allFilteredEvents]);

  useEffect(() => {
    const onRiskEvidence = (data: RiskEvidenceWsPayload) => {
      if (data?.event_id == null) return;
      if (poolId && data.pool_id != null && String(data.pool_id) !== String(poolId)) {
        return;
      }
      const badge = mapEvidenceManifestStatus(String(data.status || ''));
      const dbId = String(data.event_id);
      setEvidenceStatusMap((prev) => {
        const next = { ...prev };
        const poolScoped = poolId
          ? events.filter((e) => String(e.poolId) === String(poolId))
          : events;
        for (const ev of poolScoped) {
          if (String(getEvidenceEventId(ev)) === dbId) {
            next[ev.id] = badge;
          }
        }
        return next;
      });
    };

    monitoringWebSocket.subscribe<RiskEvidenceWsPayload>('risk_evidence', onRiskEvidence);
    return () => {
      monitoringWebSocket.unsubscribe('risk_evidence', onRiskEvidence);
    };
  }, [events, poolId]);

  const getCctvLabel = (event: Event): string | undefined => {
    const cctvId = event.metadata?.cctv_id != null ? String(event.metadata.cctv_id) : '';
    const cctvCode =
      event.metadata?.cctv_code != null
        ? String(event.metadata.cctv_code)
        : typeof event.location === 'string' && /^CCTV-\d+$/i.test(event.location.trim())
          ? event.location.trim()
          : '';
    const mappedById = cctvId ? cctvLocationMap[cctvId] : '';
    const mappedByCode =
      cctvCode && event.poolId
        ? cctvLocationMap[`pool:${event.poolId}:code:${cctvCode.toUpperCase()}`]
        : '';
    const riskLocRaw = String((event.metadata?.risk_evidence_location as string) || '').trim();
    const locationRaw = String(event.location || '').trim();
    const riskLoc =
      /^\d+-\d+$/.test(riskLocRaw) || /^CCTV-\d+$/i.test(riskLocRaw) ? riskLocRaw : '';
    const fallbackLoc =
      /^\d+-\d+$/.test(locationRaw) || /^CCTV-\d+$/i.test(locationRaw) ? locationRaw : '';
    const loc = mappedById || mappedByCode || riskLoc || fallbackLoc || '';
    const cleaned = String(loc || '').trim();
    const pool = String(event.poolName || '').trim();
    if (!cleaned || cleaned === '-' || cleaned === '—') return undefined;
    if (pool && cleaned === pool) return undefined;
    return cleaned;
  };
  const getPoolLabel = (event: Event) => {
    const p = String(event.poolName || '').trim();
    return p && p !== '-' && p !== '—' ? p : `시설 #${event.poolId}`;
  };

  const isTechnicalEventKey = (s: string) => /^[a-z][a-z0-9_]*$/i.test(s) && s.includes('_');

  /** 목록에 쓰지 않을 기본 위험유형 코드·라벨 */
  const isDefaultRiskTypeNoise = (raw: string) => {
    const s = String(raw || '').trim();
    if (!s) return true;
    const lower = s.toLowerCase().replace(/\s+/g, '');
    const codes = new Set([
      'drowning',
      'drowning_risk',
      'drowning_risk_motion',
      'motion_risk',
      'no_motion',
      'sinking',
      'manual',
      'safety_alert',
      'risk',
    ]);
    if (codes.has(s.toLowerCase())) return true;
    return /^(익사|익사위험|위험동작|위험행동|움직임없음|가라앉음|위험)$/.test(lower);
  };

  type EventListRowColumns = {
    eventLabel: string;
    pool: string;
    cctv: string;
    time: string;
  };

  /**
   * 목록 「내용」열 — bbox별 이벤트 코드명을 모두 나열.
   * 알림 초안("<위험> … 이벤트 발생…")은 목록에 쓰지 않음.
   */
  const buildEventListColumns = (event: Event): EventListRowColumns => {
    const riskLabelMeta = String((event.metadata?.risk_label as string) || '').trim();
    const riskLabelsMeta = Array.isArray(event.metadata?.risk_labels)
      ? (event.metadata!.risk_labels as unknown[])
          .map((x) => String(x || '').trim())
          .filter(Boolean)
      : [];
    const riskItemsMeta = Array.isArray(event.metadata?.risk_items)
      ? (event.metadata!.risk_items as unknown[])
      : [];
    const titleRaw = String(event.title || '').trim();
    const descRaw = String(event.description || '').trim();
    const poolLabel = getPoolLabel(event);
    const cctvLabel = getCctvLabel(event) || '';

    const pushCode = (raw: string, out: string[]) => {
      const t = raw.trim();
      if (!t || isTechnicalEventKey(t) || isDefaultRiskTypeNoise(t)) return;
      // 알림 헤더·태그 조각 제외
      if (/^<[^>]+>$/.test(t)) return;
      if (/이벤트 발생/.test(t) || /즉시 현장 확인/.test(t)) return;
      if (!out.includes(t)) out.push(t);
    };

    const codes: string[] = [];
    for (const c of [riskLabelMeta, ...riskLabelsMeta]) pushCode(c, codes);
    for (const item of riskItemsMeta) {
      if (item && typeof item === 'object') {
        const lab = String((item as { label?: unknown }).label || '').trim();
        if (lab) pushCode(lab, codes);
      }
    }

    // 메시지 본문 "1) 코드 — 설명" 줄에서도 수집 (메타에 일부만 있을 때 보완)
    if (descRaw) {
      for (const line of descRaw.split(/\r?\n/)) {
        const m = line.trim().match(/^\d+\)\s*(.+?)(?:\s*[—\-–]\s*.*)?$/);
        if (m?.[1]) pushCode(m[1], codes);
      }
    }

    if (codes.length === 0 && titleRaw) {
      pushCode(titleRaw, codes);
    }

    let eventLabel = codes.join(' · ') || '이벤트';
    if (eventLabel.length > 120) {
      eventLabel = `${eventLabel.slice(0, 120).trim()}…`;
    }

    return {
      eventLabel,
      pool: poolLabel,
      cctv: cctvLabel || '—',
      time: formatEventTimeLabel(event.timestamp),
    };
  };

  const eventRowGridSx = {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    // 내용 | 시설 | CCTV | 일시(축소) | 상태
    gridTemplateColumns: {
      xs: 'minmax(0, 1.7fr) minmax(5.75rem, 0.95fr) minmax(3rem, 0.4fr) minmax(9.5rem, 0.55fr) minmax(10rem, 0.9fr)',
    },
    columnGap: 1.5,
    alignItems: 'center',
  } as const;

  const eventRowCellSx = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    lineHeight: 1.5,
    fontSize: '0.875rem',
  };

  const renderEvidenceBadge = (event: Event) => {
    const eventType = resolveEvidenceEventType(event);
    if (!isDrowningEventType(eventType)) return null;
    const status = evidenceStatusMap[event.id];
    if (status === 'ready') {
      return <Chip label="증거완료" size="small" color="success" sx={{ ml: 0.5, height: 20, fontSize: '0.68rem' }} />;
    }
    if (status === 'archiving') {
      return <Chip label="증거저장중" size="small" color="warning" sx={{ ml: 0.5, height: 20, fontSize: '0.68rem' }} />;
    }
    if (status === 'empty') {
      return <Chip label="증거없음" size="small" color="default" sx={{ ml: 0.5, height: 20, fontSize: '0.68rem' }} />;
    }
    return <Chip label="증거대기" size="small" variant="outlined" sx={{ ml: 0.5, height: 20, fontSize: '0.68rem' }} />;
  };

  const renderEventList = (listEvents: Event[]) => (
    <List sx={{ px: 0, width: '100%', overflow: 'hidden', py: 0.5 }}>
      {listEvents.length > 0 ? (
        <Box
          component="li"
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            listStyle: 'none',
          }}
        >
          {showBulkDelete ? <Box sx={{ width: 42, flexShrink: 0 }} /> : null}
          <Box sx={eventRowGridSx}>
            <Typography variant="caption" color="text.secondary" sx={eventRowCellSx}>
              내용
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={eventRowCellSx}>시설</Typography>
            <Typography variant="caption" color="text.secondary" sx={eventRowCellSx}>
              CCTV
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={eventRowCellSx}>
              일시
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, minWidth: '10rem', textAlign: 'right' }}>
              상태
            </Typography>
          </Box>
        </Box>
      ) : null}
      {listEvents.map((event, index) => {
        const cols = buildEventListColumns(event);
        const rel = formatRelativeTime(event.timestamp);
        const summaryTooltip = [
          cols.eventLabel,
          cols.pool,
          cols.cctv,
          rel ? `${cols.time} (${rel})` : cols.time,
          String(event.description || '').trim(),
        ]
          .filter(Boolean)
          .filter((v, i, arr) => arr.indexOf(v) === i)
          .join(' · ');
        return (
          <React.Fragment key={event.id}>
            <ListItem
              alignItems="center"
              sx={{
                px: 1,
                py: 0.75,
                overflow: 'hidden',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
              }}
              onClick={() => handleEventClick(event)}
            >
              {showBulkDelete ? (
                <Checkbox
                  size="small"
                  checked={selected.has(event.id)}
                  disabled={bulkBusy}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleOne(event.id)}
                  sx={{ flexShrink: 0, p: 0.5, mr: 0.5, alignSelf: 'center' }}
                />
              ) : null}
              <Tooltip title={summaryTooltip}>
                <Box sx={eventRowGridSx}>
                  <Box
                    sx={{
                      ...eventRowCellSx,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    {event.imageUrl ? (
                      <EventSnapshotThumb
                        src={
                          snapshotThumbBust[event.id]
                            ? `${event.imageUrl}${event.imageUrl.includes('?') ? '&' : '?'}t=${snapshotThumbBust[event.id]}`
                            : event.imageUrl
                        }
                        width={42}
                        height={26}
                      />
                    ) : null}
                    <Typography
                      component="span"
                      color="text.primary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: !event.isRead ? 600 : 400,
                      }}
                    >
                      {cols.eventLabel}
                    </Typography>
                  </Box>
                  <Typography component="span" color="text.primary" sx={eventRowCellSx}>
                    {cols.pool}
                  </Typography>
                  <Typography
                    component="span"
                    color={cols.cctv === '—' ? 'text.disabled' : 'text.primary'}
                    sx={eventRowCellSx}
                  >
                    {cols.cctv}
                  </Typography>
                  <Typography component="span" color="text.secondary" sx={eventRowCellSx}>
                    {cols.time}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderEvidenceBadge(event)}
                    {(() => {
                      const dbId = resolveEventDbId(event);
                      const cnt = dbId != null ? safetyAlertCounts[dbId] ?? 0 : 0;
                      const hasAlerts = dbId != null && cnt > 0;
                      return (
                        <Tooltip
                          title={
                            hasAlerts ? `현장 안전 알림 ${cnt}건` : '현장 알림 없음'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!hasAlerts}
                              aria-label={
                                hasAlerts
                                  ? `현장 안전 알림 ${cnt}건`
                                  : '현장 알림 없음'
                              }
                              sx={{
                                p: 0.25,
                                color: hasAlerts ? 'primary.main' : 'action.disabled',
                              }}
                              onClick={() => {
                                if (dbId == null || !hasAlerts) return;
                                setSafetyAlertDialog({
                                  eventId: dbId,
                                  title: cols.eventLabel,
                                });
                              }}
                            >
                              <SmsIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    <Chip
                      label={eventStatusChipLabel(event)}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        borderColor: 'divider',
                        color: 'text.secondary',
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            </ListItem>
            {index < listEvents.length - 1 ? (
              <Divider component="li" sx={{ my: 0.5 }} />
            ) : null}
          </React.Fragment>
        );
      })}
    </List>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: fillHeight ? '100%' : 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          overflow: 'hidden',
          flex: fillHeight ? 1 : undefined,
          display: 'flex',
          flexDirection: 'column',
          minHeight: fillHeight ? 0 : undefined,
          '&:last-child': { pb: 2 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div" gutterBottom>
              {title}
              {unreadCount > 0 && (
                <Chip 
                  size="small" 
                  color="error" 
                  label={unreadCount} 
                  sx={{ ml: 1, height: 20, minWidth: 20 }} 
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {showFilter && (
              <Tooltip title={activeFilters.onlyUnread ? "모든 이벤트 보기" : "읽지 않은 이벤트만"}>
                <IconButton 
                  size="small" 
                  color={activeFilters.onlyUnread ? "primary" : "default"}
                  onClick={toggleUnreadFilter}
                >
                  <FilterIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {showRefresh && (
              <Tooltip title="새로고침">
                <IconButton 
                  size="small" 
                  onClick={handleRefresh}
                  disabled={loading || localLoading || bulkBusy}
                >
                  {loading || localLoading ? 
                    <CircularProgress size={20} /> : 
                    <RefreshIcon />
                  }
                </IconButton>
              </Tooltip>
            )}

            {showBulkDelete && (
              <>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  disabled={bulkBusy || selected.size === 0}
                  onClick={() => void handleBulkDeleteSelected()}
                >
                  선택 삭제
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={bulkBusy || allFilteredEvents.length === 0}
                  onClick={() => void handleDeleteAllEvents()}
                >
                  전체 삭제
                </Button>
              </>
            )}
            {allowOrphanPurge && (
              <Tooltip title="이벤트 목록에 없는 증거 폴더(_staging_·삭제된 이벤트)만 디스크에서 일괄 삭제">
                <span>
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    startIcon={
                      orphanBusy ? <CircularProgress size={14} /> : <FolderDeleteOutlinedIcon />
                    }
                    disabled={bulkBusy || orphanBusy}
                    onClick={() => void handleOpenOrphanEvidencePurge()}
                  >
                    미연결 증거 정리
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        {showBulkDelete && visibleEvents.length > 0 && !loading && !localLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, pl: 0.5 }}>
            <Checkbox
              size="small"
              indeterminate={someSelected}
              checked={allSelected}
              onChange={toggleAll}
              disabled={bulkBusy}
            />
            <Typography variant="caption" color="text.secondary">
              전체 선택 ({selected.size}/{visibleEvents.length})
            </Typography>
          </Box>
        ) : null}

        {loading || localLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : allFilteredEvents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              표시할 이벤트 없음
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              onWheel={paginated ? undefined : handleListWheel}
              sx={{
                maxHeight: paginated ? undefined : listMaxHeight,
                overflowY: paginated ? 'visible' : 'auto',
                overflowX: 'hidden',
                flex: fillHeight ? 1 : undefined,
                minHeight: fillHeight ? 0 : undefined,
                overscrollBehaviorY: 'auto',
              }}
            >
              {renderEventList(visibleEvents)}
            </Box>
            {paginated ? (
              <WidgetListPagination
                page={safePage}
                pageCount={pageCount}
                totalItems={allFilteredEvents.length}
                pageSize={pageSize!}
                onPageChange={setListPage}
                onOpenFullView={() => setFullListOpen(true)}
                disabled={loading || localLoading || bulkBusy}
              />
            ) : null}
          </>
        )}
        
        {showViewAll && !paginated && events.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button 
              component={RouterLink} 
              to="/alerts" 
              size="small" 
              endIcon={<NotificationsIcon />}
            >
              모든 이벤트 보기 ({events.length})
            </Button>
          </Box>
        )}
      </CardContent>

      <Dialog
        open={fullListOpen}
        onClose={() => setFullListOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{fullViewTitle}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '70vh', overflowY: 'auto', px: 1, py: 0.5 }}>
            {allFilteredEvents.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                표시할 이벤트 없음
              </Typography>
            ) : (
              renderEventList(allFilteredEvents)
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullListOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detailEvent)}
        onClose={() => setDetailEvent(null)}
        maxWidth="lg"
        fullWidth
      >
        {detailEvent && (
          <>
            <DialogTitle>
              {detailEvent.title}
              <Typography variant="body2" color="text.secondary">
                {getPoolLabel(detailEvent)}
                {getCctvLabel(detailEvent) ? ` · ${getCctvLabel(detailEvent)}` : ''}
                {' · '}
                {formatAbsoluteTime(detailEvent.timestamp)}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {detailEvent.imageUrl && resolveEventDbId(detailEvent) ? (
                <EventSnapshotBboxEditPanel
                  eventId={resolveEventDbId(detailEvent)!}
                  initialMessage={detailEvent.description || ''}
                  onSaved={({ imageUrl, message, riskLabel }) => {
                    setDetailEvent((prev) =>
                      prev
                        ? {
                            ...prev,
                            imageUrl,
                            ...(message != null ? { description: message } : {}),
                            ...(riskLabel ? { title: riskLabel } : {}),
                          }
                        : prev
                    );
                    setSnapshotThumbBust((m) => ({
                      ...m,
                      [detailEvent.id]: Date.now(),
                    }));
                    const dbId = resolveEventDbId(detailEvent);
                    patchEventDisplay(
                      detailEvent.id,
                      {
                        imageUrl,
                        ...(message != null ? { description: message } : {}),
                        ...(riskLabel ? { title: riskLabel } : {}),
                      },
                      dbId ?? undefined
                    );
                  }}
                />
              ) : detailEvent.imageUrl ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    발생 시점 캡처
                  </Typography>
                  <EventSnapshotThumb
                    src={detailEvent.imageUrl}
                    large
                    showDownload
                    alt={`${detailEvent.title} 캡처`}
                  />
                </Box>
              ) : null}
              {isDrowningEventType(resolveEvidenceEventType(detailEvent)) && (
                <RiskEvidencePlayer
                  eventId={
                    getEvidenceEventId(detailEvent)
                  }
                  eventType={resolveEvidenceEventType(detailEvent)}
                  cctvId={
                    detailEvent.metadata?.cctv_id != null
                      ? Number(detailEvent.metadata.cctv_id)
                      : undefined
                  }
                  poolName={getPoolLabel(detailEvent)}
                  locationLabel={
                    getCctvLabel(detailEvent) || ''
                  }
                  eventTimestamp={
                    detailEvent.metadata?.trigger_ts != null &&
                    Number.isFinite(Number(detailEvent.metadata.trigger_ts))
                      ? Number(detailEvent.metadata.trigger_ts)
                      : detailEvent.timestamp
                  }
                  rotate180={Boolean(detailEvent.metadata?.rotate_180)}
                  onStatusChange={(status) => setEvidenceStatus(detailEvent.id, status)}
                  minHeight={300}
                />
              )}
              {!resolveEventDbId(detailEvent) ? (
                <Typography sx={{ mt: 2 }}>{detailEvent.description}</Typography>
              ) : null}
            </DialogContent>
            <DialogActions>
              {detailEvent.imageUrl ? (
                <Button
                  startIcon={<DownloadIcon />}
                  href={detailEvent.imageUrl}
                  download
                  onClick={async (e) => {
                    e.preventDefault();
                    const { getBearerTokenForApi } = await import('../../utils/poolSiteAuth');
                    const token = getBearerTokenForApi();
                    const headers: HeadersInit = {};
                    if (token) headers.Authorization = `Bearer ${token}`;
                    try {
                      const res = await fetch(detailEvent.imageUrl!, {
                        headers,
                        credentials: 'include',
                      });
                      if (!res.ok) return;
                      const blob = await res.blob();
                      const u = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = u;
                      a.download = `event-${resolveEventDbId(detailEvent) || detailEvent.id}-snapshot.jpg`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(u);
                    } catch {
                      /* ignore */
                    }
                  }}
                >
                  캡처 다운로드
                </Button>
              ) : null}
              <Button color="error" onClick={() => handleDeleteEvent(detailEvent)}>
                이벤트 삭제
              </Button>
              <Button onClick={() => setDetailEvent(null)}>닫기</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={orphanDialogOpen}
        onClose={() => !orphanBusy && setOrphanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>미연결 증거 일괄 정리</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            현재 이벤트 리스트(DB)에 남아 있는 이벤트의 증거 폴더는 그대로 두고,
            연결되지 않은 <code>_staging_*</code> 및 삭제된 이벤트 폴더만 디스크에서
            삭제. 최근 10분 이내 스테이징은 보호됨
          </DialogContentText>
          {orphanPreview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                유지 이벤트: {(orphanPreview.kept_event_ids ?? 0).toLocaleString()}건 · 유지
                폴더: {(orphanPreview.kept_dirs ?? 0).toLocaleString()}개
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                삭제 대상: {(orphanPreview.orphan_dirs ?? 0).toLocaleString()}개 폴더 (
                {formatEvidenceBytes(orphanPreview.orphan_bytes ?? 0)})
              </Typography>
              {(orphanPreview.skipped_recent_staging ?? 0) > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  최근 스테이징 보호: {orphanPreview.skipped_recent_staging}개
                </Typography>
              )}
              {(orphanPreview.samples?.length ?? 0) > 0 && (
                <Box
                  component="ul"
                  sx={{
                    mt: 1,
                    pl: 2,
                    maxHeight: 160,
                    overflow: 'auto',
                    fontSize: 12,
                    color: 'text.secondary',
                  }}
                >
                  {orphanPreview.samples!.slice(0, 15).map((s) => (
                    <li key={s.path || s.name}>
                      [{s.kind}] {s.name} ({formatEvidenceBytes(s.bytes ?? 0)})
                    </li>
                  ))}
                  {(orphanPreview.orphan_dirs ?? 0) > 15 ? (
                    <li>… 외 {(orphanPreview.orphan_dirs! - 15).toLocaleString()}개</li>
                  ) : null}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrphanDialogOpen(false)} disabled={orphanBusy}>
            취소
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={orphanBusy || (orphanPreview?.orphan_dirs ?? 0) === 0}
            onClick={() => void handleConfirmOrphanEvidencePurge()}
          >
            {orphanBusy ? '삭제 중…' : '삭제 실행'}
          </Button>
        </DialogActions>
      </Dialog>

      <SafetyAlertByEventDialog
        open={safetyAlertDialog != null}
        onClose={() => setSafetyAlertDialog(null)}
        eventId={safetyAlertDialog?.eventId ?? null}
        eventTitle={safetyAlertDialog?.title}
      />
    </Card>
  );
};

export default EventLogWidget; 